/**
 * Tondin Theme Fixes - Correções de erros JavaScript do tema Shopify Focal
 *
 * CORREÇÕES IMPLEMENTADAS:
 * 1. QuickBuyDrawer null innerHTML protection
 * 2. ProductVariants toggleAttribute null protection
 * 3. ProductRecommendations hasChildNodes null protection
 * 4. Flickity arrow cursor removal
 * 5. Sandbox warnings filter
 * 6. CSP frame-ancestors handler
 * 7. Bundle width settings
 * 8. Variant change debug monitoring
 */

(function() {
  'use strict';

  const DEBUG = true;

  function log(...args) {
    console.log('%c[TondinFixes]', 'color: #ff5722; font-weight: bold;', ...args);
  }

  // ==========================================================================
  // FIX 1: OPENABLE ELEMENT NULL PROTECTION
  // ==========================================================================

  function patchOpenableElement() {
    if (!window.customElements?.get('openable-element')) {
      setTimeout(patchOpenableElement, 100);
      return;
    }

    const OpenableElementClass = window.customElements.get('openable-element');
    if (!OpenableElementClass) return;

    const originalLoad = OpenableElementClass.prototype._load;

    OpenableElementClass.prototype._load = async function() {
      if (!this.requiresLoading) return;

      try {
        const startEvent = new CustomEvent('openable-element:load:start', { bubbles: false });
        this.dispatchEvent(startEvent);

        const href = this.getAttribute('href');
        if (!href) {
          throw new Error('OpenableElement: href attribute is required');
        }

        const response = await fetch(href);
        if (!response.ok) {
          throw new Error(`OpenableElement: fetch failed with status ${response.status}`);
        }

        const htmlText = await response.text();
        const element = document.createElement('div');
        element.innerHTML = htmlText;

        const tagName = this.tagName.toLowerCase();
        const targetElement = element.querySelector(tagName);

        if (!targetElement) {
          // Fallback: buscar conteúdo alternativo
          const fallbackContent = element.querySelector(
            '[data-drawer-content], [data-popover-content], .drawer__content, .popover__content'
          );

          if (fallbackContent) {
            this.innerHTML = fallbackContent.innerHTML;
          } else {
            this.innerHTML = htmlText;
          }
        } else {
          this.innerHTML = targetElement.innerHTML;
        }

        this.removeAttribute('href');

        const endEvent = new CustomEvent('openable-element:load:end', { bubbles: false });
        this.dispatchEvent(endEvent);

      } catch (error) {
        console.error('[TondinFixes] OpenableElement error:', error.message);
        throw error;
      }
    };

    log('OpenableElement patched');
  }

  // ==========================================================================
  // FIX 2: QUICKBUYDRAWER VARIANT CHANGED PROTECTION
  // ==========================================================================

  function patchQuickBuyDrawer() {
    if (!window.customElements?.get('quick-buy-drawer')) {
      setTimeout(patchQuickBuyDrawer, 100);
      return;
    }

    const QuickBuyDrawerClass = window.customElements.get('quick-buy-drawer');
    if (!QuickBuyDrawerClass) return;

    const originalOnVariantChanged = QuickBuyDrawerClass.prototype._onVariantChanged;

    QuickBuyDrawerClass.prototype._onVariantChanged = function(event) {
      try {
        if (!event?.detail) return;

        if (originalOnVariantChanged) {
          originalOnVariantChanged.call(this, event);
        }
      } catch (error) {
        console.warn('[TondinFixes] QuickBuyDrawer error:', error.message);
      }
    };

    log('QuickBuyDrawer patched');
  }

  // ==========================================================================
  // FIX 3: PRODUCT VARIANTS TOGGLE ATTRIBUTE PROTECTION
  // ==========================================================================

  function patchProductVariants() {
    if (!window.customElements?.get('product-variants')) {
      setTimeout(patchProductVariants, 100);
      return;
    }

    const ProductVariantsClass = window.customElements.get('product-variants');
    if (!ProductVariantsClass) return;

    const originalUpdateDisableSelectors = ProductVariantsClass.prototype._updateDisableSelectors;

    if (originalUpdateDisableSelectors) {
      ProductVariantsClass.prototype._updateDisableSelectors = function(selectedVariant) {
        try {
          if (!this.optionSelectors?.length) return;

          const safeApplyClass = (selector, valueIndex, available, hasAtLeastOneCombination) => {
            if (!selector) return;

            const selectorType = selector.getAttribute('data-selector-type');
            let cssSelector = '';

            switch (selectorType) {
              case 'color':
              case 'variant-image':
                cssSelector = `.variant-swatch:nth-child(${valueIndex + 1})`;
                break;
              case 'block':
                cssSelector = `.block-swatch:nth-child(${valueIndex + 1})`;
                break;
              case 'dropdown':
                cssSelector = `.combo-box__option-item:nth-child(${valueIndex + 1})`;
                break;
            }

            const element = selector.querySelector(cssSelector);
            if (!element) return;

            element.toggleAttribute('hidden', !hasAtLeastOneCombination);
            if (this.hideSoldOutVariants) {
              element.toggleAttribute('hidden', !available);
            } else {
              element.classList.toggle('is-disabled', !available);
            }
          };

          if (this.optionSelectors?.[0] && this.product?.options) {
            const options = this.product.options;
            const variants = this.product.variants;
            const selectedOption1 = selectedVariant?.option1;
            const selectedOption2 = selectedVariant?.option2;

            if (options[0]?.values) {
              options[0].values.forEach((value, valueIndex) => {
                const hasAtLeastOneCombination = variants.some(v => v.option1 === value);
                const hasAvailableVariant = variants.some(v => v.option1 === value && v.available);
                safeApplyClass(this.optionSelectors[0], valueIndex, hasAvailableVariant, hasAtLeastOneCombination);

                if (this.optionSelectors[1] && options[1]?.values && selectedOption1) {
                  options[1].values.forEach((value2, valueIndex2) => {
                    const hasAtLeastOneCombination2 = variants.some(v =>
                      v.option2 === value2 && v.option1 === selectedOption1
                    );
                    const hasAvailableVariant2 = variants.some(v =>
                      v.option2 === value2 && v.option1 === selectedOption1 && v.available
                    );
                    safeApplyClass(this.optionSelectors[1], valueIndex2, hasAvailableVariant2, hasAtLeastOneCombination2);

                    if (this.optionSelectors[2] && options[2]?.values && selectedOption2) {
                      options[2].values.forEach((value3, valueIndex3) => {
                        const hasAtLeastOneCombination3 = variants.some(v =>
                          v.option3 === value3 &&
                          v.option1 === selectedOption1 &&
                          v.option2 === selectedOption2
                        );
                        const hasAvailableVariant3 = variants.some(v =>
                          v.option3 === value3 &&
                          v.option1 === selectedOption1 &&
                          v.option2 === selectedOption2 &&
                          v.available
                        );
                        safeApplyClass(this.optionSelectors[2], valueIndex3, hasAvailableVariant3, hasAtLeastOneCombination3);
                      });
                    }
                  });
                }
              });
            }
          }
        } catch (error) {
          // Silenciar erros não críticos
        }
      };

      log('ProductVariants patched');
    }
  }

  // ==========================================================================
  // FIX 4: SANDBOX WARNINGS FILTER
  // ==========================================================================

  function silenceSandboxWarnings() {
    const originalWarn = console.warn;
    const warningsToFilter = [
      'In a sandboxed environment',
      'CLI hot reload disabled',
      'HotReload'
    ];

    console.warn = function(...args) {
      const message = args.join(' ');
      const shouldFilter = warningsToFilter.some(filter => message.includes(filter));

      if (!shouldFilter) {
        originalWarn.apply(console, args);
      }
    };

    log('Sandbox warnings filter applied');
  }

  // ==========================================================================
  // FIX 5: CSP FRAME-ANCESTORS HANDLER
  // ==========================================================================

  function handleCSPErrors() {
    window.addEventListener('securitypolicyviolation', function(event) {
      if (event.violatedDirective === 'frame-ancestors') {
        event.stopImmediatePropagation();
      }
    });

    log('CSP handler configured');
  }

  // ==========================================================================
  // FIX 6: FLICKITY ARROW CURSOR REMOVAL
  // ==========================================================================

  function disableFlickityArrowCursor() {
    function removeHoveringClasses(element) {
      if (element?.classList) {
        element.classList.remove('is-hovering-right', 'is-hovering-left');
      }
    }

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('is-hovering-right') || target.classList.contains('is-hovering-left')) {
            removeHoveringClasses(target);
          }
        }
      });
    });

    function observeFlickityElements() {
      document.querySelectorAll('.flickity-enabled, [data-flickity]').forEach(function(element) {
        observer.observe(element, {
          attributes: true,
          attributeFilter: ['class']
        });
        removeHoveringClasses(element);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeFlickityElements);
    } else {
      observeFlickityElements();
    }

    window.addEventListener('load', function() {
      setTimeout(observeFlickityElements, 500);
    });

    log('Flickity arrow cursor disabled');
  }

  // ==========================================================================
  // FIX 7: BUNDLE WIDTH SETTINGS
  // ==========================================================================

  function applyBundleWidthSettings() {
    const bundles = document.querySelectorAll(
      '.bundle-compre-junto, .compre-junto-section, .bundle-container'
    );

    bundles.forEach(bundle => {
      const settings = bundle.closest('[data-section-settings]');
      if (!settings) return;

      try {
        const config = JSON.parse(settings.getAttribute('data-section-settings'));

        if (config.layout_width) {
          bundle.setAttribute('data-width', config.layout_width);

          if (config.layout_width === 'custom' && config.custom_width) {
            bundle.style.setProperty('--custom-width-value', `${config.custom_width}px`);
          }
        }
      } catch (e) {
        // Ignore parse errors
      }

      // Forçar largura máxima quando dentro da coluna de informações
      const isInProductInfo = bundle.closest('.product__info-wrapper');
      if (isInProductInfo) {
        bundle.style.maxWidth = '550px';
      }
    });
  }

  // ==========================================================================
  // FIX 8: REMOVED - Variant change interception was causing conflicts
  // ==========================================================================

  function setupVariantChangeDebug() {
    // REMOVIDO: Toda lógica de interceptação de variantes foi removida
    // O tema nativo agora controla a galeria de imagens
    log('Variant debug: using native theme behavior');
  }

  // ==========================================================================
  // INICIALIZAÇÃO
  // ==========================================================================

  function initialize() {
    log('Initializing...');

    try {
      // Fixes que não dependem de custom elements
      silenceSandboxWarnings();
      handleCSPErrors();
      disableFlickityArrowCursor();

      // Patches de custom elements
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          patchOpenableElement();
          patchQuickBuyDrawer();
          patchProductVariants();
          applyBundleWidthSettings();
          setupVariantChangeDebug();
        });
      } else {
        patchOpenableElement();
        patchQuickBuyDrawer();
        patchProductVariants();
        applyBundleWidthSettings();
        setupVariantChangeDebug();
      }

      log('Initialization complete');

    } catch (error) {
      console.error('[TondinFixes] Initialization error:', error.message);
    }
  }

  // Executar
  initialize();

  // Shopify section reload
  document.addEventListener('shopify:section:load', applyBundleWidthSettings);

})();
